import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import zh from "./locales/zh.json";
import ar from "./locales/ar.json";
import fa from "./locales/fa.json";

/**
 * Janté i18n configuration.
 *
 * Strategy: ship a hand-crafted "core strings" bundle for every language
 * (nav, footer, login/register, key buttons, consents). Sasha's actual chat
 * replies stay in whatever language Claude decides to use — Claude Sonnet 4.5
 * naturally responds in the visitor's language when they type in it, so we
 * don't need to translate her output ourselves.
 *
 * Languages: English (default), Spanish, French, Simplified Chinese, Arabic
 * (RTL), Farsi/Persian (RTL). Auto-detection runs once on first visit (URL ?
 * lang= → localStorage → navigator.language → HTML lang attr). The user's
 * choice is remembered in localStorage.
 *
 * RTL: `document.documentElement.dir` is flipped to "rtl" when Arabic or
 * Farsi is selected. See `LanguageSwitcher` for the toggle.
 */

export const LANGS = [
  { code: "en", label: "English",   short: "EN", rtl: false, flag: "🇺🇸" },
  { code: "es", label: "Español",   short: "ES", rtl: false, flag: "🇪🇸" },
  { code: "fr", label: "Français",  short: "FR", rtl: false, flag: "🇫🇷" },
  { code: "zh", label: "中文",      short: "中",  rtl: false, flag: "🇨🇳" },
  { code: "ar", label: "العربية",   short: "ع",   rtl: true,  flag: "🇸🇦" },
  { code: "fa", label: "فارسی",    short: "ف",   rtl: true,  flag: "🇮🇷" },
];

export const RTL_LANGS = new Set(LANGS.filter((l) => l.rtl).map((l) => l.code));

/** Flip `<html dir="…">` and `<html lang="…">` so Tailwind / CSS-logical
 *  properties pick up RTL-aware spacing automatically. Called by both i18n
 *  init and the LanguageSwitcher click handler. */
export function applyHtmlDirForLang(code) {
  if (typeof document === "undefined") return;
  const dir = RTL_LANGS.has(code) ? "rtl" : "ltr";
  document.documentElement.dir = dir;
  document.documentElement.lang = code;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      zh: { translation: zh },
      ar: { translation: ar },
      fa: { translation: fa },
    },
    fallbackLng: "en",
    supportedLngs: LANGS.map((l) => l.code),
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ["querystring", "localStorage", "navigator", "htmlTag"],
      lookupQuerystring: "lang",
      lookupLocalStorage: "jante_lang",
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

// Apply RTL/LTR on first load + whenever language changes downstream.
applyHtmlDirForLang(i18n.language || "en");
i18n.on("languageChanged", (lng) => applyHtmlDirForLang(lng));

export default i18n;
